import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

/**
 * Last net before a blank screen: without this guard, any error thrown during
 * render (typically a failed dynamic import) unmounts the React tree and
 * leaves an empty page with no message at all.
 */
class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-lg font-semibold">Une erreur est survenue</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          La page n'a pas pu se charger complètement. Recharger suffit
          généralement à résoudre le problème.
        </p>
        <Button onClick={() => window.location.reload()}>
          Recharger la page
        </Button>
      </div>
    );
  }
}

export default AppErrorBoundary;
